import { applyMiddleware, createStore, combineReducers } from "redux";
import { composeWithDevTools } from "redux-devtools-extension";
import createSagaMiddleware from "redux-saga";
import { Reducer } from "redux";
import { errors } from "./reducers/errors";
import { all, spawn, call, put } from "redux-saga/effects";
import { ShellProps, ReduxDefinition } from "../ModuleProps";
import { History } from "history";
import { API_ERROR } from "./actions";

export const configureStore = (
  props: ShellProps,
  history: History
): ReturnType<typeof createStore> => {
  const { auth, items = [], toolbarItems = [], coreItems = [] } = props;

  const authSaga = auth.module.redux.saga;

  const sagas = [
    authSaga,
    ...findSagas(items),
    ...findSagas(toolbarItems),
    ...findSagas(coreItems),
  ];

  function* rootSaga() {
    yield all(
      sagas.map((saga) =>
        spawn(function* () {
          while (true) {
            try {
              yield call(saga);
              break;
            } catch (e) {
              yield put({ type: API_ERROR, payload: e });
            }
          }
        })
      )
    );
  }
  const sagaMiddleware = createSagaMiddleware({ context: { history } });

  const reducers = {
    errors,
    [auth.module.redux.name]: auth.module.redux.reducer,
    ...findReducers(items),
    ...findReducers(toolbarItems),
    ...findReducers(coreItems),
  };

  const store = createStore(
    combineReducers(reducers),
    composeWithDevTools(applyMiddleware(sagaMiddleware))
  );

  sagaMiddleware.run(rootSaga);

  return store;
};

export const findReducers = (
  items: {
    module?: {
      redux?: ReduxDefinition;
    };
  }[]
) => {
  const reducersArray = items
    .filter((item) => item.module)
    .map((item) => item.module?.redux);

  const reducersObject = reducersArray.reduce((acc, curr) => {
    if (curr) {
      const name = curr.name;
      if (acc[name]) {
        throw new Error(
          `Module root reducer with name ${name} is present more than once in module definitions.  All names must be unique.`
        );
      }
      acc[name] = curr.reducer;
    }
    return acc;
  }, {} as { [key: string]: Reducer });

  return reducersObject;
};

export const findSagas = (
  items: {
    module?: {
      redux?: ReduxDefinition;
    };
  }[]
) => {
  return items
    .filter((item) => item.module)
    .map((item) => item.module?.redux?.saga)
    .filter((saga) => !!saga);
};
